import { useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';

import {
	AUTH_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	IS_SUBSCRIBED_KEY,
} from '@shared/auth/Authentication.hooks';
import {
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	APP_USER_ID,
} from '@/constants';

import config from '@/config';
import { AuthCognito } from '@/config.type';
import { AppContext } from '@/App.context';
import { createCognitoAuth } from '@shared/auth/cognito';
import { clearTokens } from '@shared/auth/tokens';

/**
 * `basic`'s Cognito configuration. Live on `scubadiver`, `theog`, `daily`,
 * `smartmags` (magazine template), and `starmagazine` per `pwa-config` — the
 * three latter templates import this module's exports directly rather than
 * having their own copy. See `services/auth/cognito.ts` for what each field
 * preserves and why.
 */
const cognitoAuth = createCognitoAuth({
	authorizePath: '/login',
	scope: 'email+openid',
	refreshTokenPath: '/auth/token',
	useBasename: () => {
		const router = useRouter();
		return `${router.basepath.replace(/\/$/, '')}/callback`;
	},
	setAuthKeyForAdmin: false,
	requireExpirationKeysPresent: false,
	expirationSource: 'jwt',
	clearTokensOnRefreshFailureInAuthEffect: false,
	tryRefreshTokenPerformsRefresh: false,
	tryRefreshTokenClearsOnInvalidSession: false,
	requireCognitoAuthType: true,
	requireAuthEnabled: true,
	buildLogoutUrl: (redirectUri) => {
		if (config?.app?.auth?.type !== 'cognito') {
			return null;
		}
		const auth = config.app.auth as AuthCognito;
		const sections = [
			`client_id=${auth.config.clientId}`,
			`logout_uri=${redirectUri}`,
		].join('&');
		return [auth.config.host, '/logout?', sections].join('');
	},
});

export const {
	useAuth,
	tryRefreshToken,
	logOut,
	refreshAccessToken,
	ensureAuthenticated,
} = cognitoAuth;

export { clearTokens };

export function useLogout(): boolean {
	const { clearCache, kv } = useContext(AppContext);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const clear = useCallback(() => {
		kv.clear();
		setIsLoading(false);
	}, [kv]);
	useEffect(() => {
		// Deliberately not the shared clearTokens() here: this list predates it
		// and omits FEATURES_KEY, unlike the OAuth callback/refresh cycle's
		// clearTokens(). Kept exact to avoid changing logout behaviour.
		const keys = [
			AUTH_KEY,
			USER_ROLE,
			ACCESS_TOKEN_KEY,
			ACCESS_TOKEN_EXPIRATION_KEY,
			REFRESH_TOKEN_KEY,
			ACCESS_TOKEN_EXPIRE_AT_KEY,
			REFRESH_TOKEN_EXPIRATION_KEY,
			APP_USER_ID,
			IS_SUBSCRIBED_KEY,
		];
		for (const k of keys) {
			if (localStorage.getItem(k)) {
				localStorage.removeItem(k);
			}
		}

		if (clearCache) {
			clearCache()
				.then(() => {
					clear();
					setIsLoading(false);
				})
				.catch(() => {
					clear();
					setIsLoading(false);
				});
			return;
		}

		setIsLoading(false);
	}, [clear, clearCache]);

	return isLoading;
}
